import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AiTextField, AiImageField, AiVideoField } from "@/components/editor/AiField";
import { AdminImageInput } from "@/components/admin/AdminImageInput";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { useState } from "react";

const OVERLAY_OPTS = [
  { value: "none", label: "None" },
  { value: "black", label: "Solid Black (darken)" },
  { value: "red", label: "Brand Red" },
  { value: "black_ltr", label: "Black Left → Right" },
];
const BTN_STYLES = [
  { value: "primary", label: "Primary (Red)" },
  { value: "secondary", label: "Secondary (Outline)" },
  { value: "light", label: "Light (White)" },
];

// Preset text-color palette (matches brand tokens). "" = use the section's default color.
const COLOR_PRESETS = [
  { value: "", label: "Default", swatch: "transparent", ring: true },
  { value: "#ffffff", label: "White", swatch: "#ffffff" },
  { value: "hsl(354 79% 51%)", label: "Brand Red", swatch: "hsl(354 79% 51%)" },
  { value: "#000000", label: "Black", swatch: "#000000" },
  { value: "#d4af37", label: "Gold", swatch: "#d4af37" },
];

// Reusable preset color picker rendered as small swatches.
const ColorField = ({ label, value = "", onChange, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      {COLOR_PRESETS.map((c) => {
        const active = (value || "") === c.value;
        return (
          <button
            key={c.value || "default"}
            type="button"
            title={c.label}
            aria-label={`${label}: ${c.label}`}
            aria-pressed={active}
            onClick={() => onChange(c.value)}
            data-testid={`${testid}-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative h-7 w-7 rounded-full border transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "border-foreground ring-2 ring-ring" : "border-border"}`}
            style={{ background: c.swatch === "transparent" ? "repeating-conic-gradient(hsl(var(--muted)) 0% 25%, transparent 0% 50%) 50% / 10px 10px" : c.swatch }}
          >
            {active && <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color: c.value === "#ffffff" || c.value === "" ? "#111" : "#fff" }}>✓</span>}
          </button>
        );
      })}
    </div>
  </div>
);
const HAS_MEDIA = new Set(["hero", "image_text", "cta_band"]);
const HAS_VIDEO = new Set(["hero", "cta_band"]);
const HAS_OVERLAY = new Set(["hero", "cta_band"]);

const ButtonsEditor = ({ buttons = [], onChange }) => {
  const update = (i, patch) => onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className="space-y-3">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Buttons / Links</Label>
      {buttons.map((b, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="space-y-2 rounded-lg border border-border p-3" data-testid={`drawer-button-${i}`}>
          <Input value={b.label || ""} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label" data-testid={`drawer-button-label-${i}`} />
          <Input value={b.href || ""} onChange={(e) => update(i, { href: e.target.value })} placeholder="Link (/path or https://…)" data-testid={`drawer-button-href-${i}`} />
          <div className="flex items-center gap-2">
            <Select value={b.style || "primary"} onValueChange={(v) => update(i, { style: v })}>
              <SelectTrigger className="h-9 flex-1" data-testid={`drawer-button-style-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="z-[90]">{BTN_STYLES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={!!b.external} onChange={(e) => update(i, { external: e.target.checked })} /> New tab
            </label>
            <button type="button" onClick={() => onChange(buttons.filter((_, j) => j !== i))} className="text-destructive hover:text-destructive/80" data-testid={`drawer-button-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <ColorField label="Button text color" value={b.textColor} onChange={(v) => update(i, { textColor: v })} testid={`drawer-button-textcolor-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...buttons, { label: "New Button", href: "#", style: "primary", external: false }])} data-testid="drawer-button-add" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors">
        <Plus className="h-4 w-4" /> Add button
      </button>
    </div>
  );
};

const SlidesEditor = ({ slides = [], onChange }) => {
  const [picker, setPicker] = useState(false);
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="hero-slides-editor">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Background slides (optional, rotates)</Label>
      {slides.length === 0 && <p className="text-xs text-muted-foreground">No slides — the single background image/video is used.</p>}
      {slides.map((s, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex items-center gap-2" data-testid={`slide-${i}`}>
          <img src={s} alt="" className="h-12 w-20 shrink-0 rounded border border-border object-cover" />
          <Input value={s} onChange={(e) => onChange(slides.map((x, j) => (j === i ? e.target.value : x)))} className="flex-1 text-xs" />
          <button type="button" onClick={() => onChange(slides.filter((_, j) => j !== i))} className="text-destructive hover:text-destructive/80" data-testid={`slide-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => setPicker(true)} data-testid="slide-add" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><Plus className="h-4 w-4" /> Add slide</button>
      <MediaPicker open={picker} onOpenChange={setPicker} onSelect={(m) => onChange([...slides, m.url])} />
    </div>
  );
};

export const SectionEditDrawer = ({ open, onOpenChange, section, pageName, onPatch, onSave, saving }) => {
  if (!section) return null;
  const c = section.content || {};
  const t = section.type;
  const setC = (patch) => onPatch("content", patch);
  const setColor = (key, v) => onPatch("content", { colors: { ...(c.colors || {}), [key]: v } });
  const setM = (patch) => onPatch("media", patch);
  const setO = (patch) => onPatch("overlay", patch);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="z-[75] w-full overflow-y-auto sm:max-w-md" data-testid="section-drawer">
        <SheetHeader>
          <SheetTitle className="display-title text-2xl capitalize">Edit {t === "hero" ? "hero" : section.label || t} — {pageName}</SheetTitle>
          <SheetDescription>Changes preview live and stay in your draft until you commit.</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5 pb-24">
          {/* Content */}
          {t === "hero" ? (
            <>
              <AiTextField label="Eyebrow" value={c.eyebrow} onChange={(v) => setC({ eyebrow: v })} field="eyebrow" sectionType={t} pageName={pageName} testid="drawer-eyebrow" />
              <AiTextField label="Title (white part)" value={c.heading_left} onChange={(v) => setC({ heading_left: v })} field="heading_left" sectionType={t} pageName={pageName} testid="drawer-heading-left" />
              <AiTextField label="Accent word (red part)" value={c.heading_right} onChange={(v) => setC({ heading_right: v })} field="heading_right" sectionType={t} pageName={pageName} testid="drawer-heading-right" />
              <AiTextField label="Supporting line" value={c.supporting_line} onChange={(v) => setC({ supporting_line: v })} field="supporting_line" sectionType={t} pageName={pageName} placeholder="e.g. MENTAL. PHYSICAL. SPIRITUAL." testid="drawer-supporting-line" />
              <AiTextField label="Subtitle" value={c.subhead} onChange={(v) => setC({ subhead: v })} rich field="subhead" sectionType={t} pageName={pageName} testid="drawer-subhead" />
              <div className="space-y-3 rounded-lg border border-border p-3" data-testid="hero-text-colors">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Text colors</Label>
                <ColorField label="Title color" value={c.colors?.heading_left} onChange={(v) => setColor("heading_left", v)} testid="drawer-color-title" />
                <ColorField label="Accent word color" value={c.colors?.heading_right} onChange={(v) => setColor("heading_right", v)} testid="drawer-color-accent" />
                <ColorField label="Supporting line color" value={c.colors?.supporting_line} onChange={(v) => setColor("supporting_line", v)} testid="drawer-color-supporting" />
                <ColorField label="Subtitle color" value={c.colors?.subhead} onChange={(v) => setColor("subhead", v)} testid="drawer-color-subhead" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foreground logo (optional)</Label>
                <AdminImageInput value={c.logo_url} onChange={(v) => setC({ logo_url: v })} testid="drawer-logo" />
              </div>
            </>
          ) : (
            <>
              <AiTextField label="Eyebrow" value={c.eyebrow} onChange={(v) => setC({ eyebrow: v })} field="eyebrow" sectionType={t} pageName={pageName} testid="drawer-eyebrow" />
              <AiTextField label="Heading" value={c.heading} onChange={(v) => setC({ heading: v })} field="heading" sectionType={t} pageName={pageName} testid="drawer-heading" />
              {(t === "rich_text" || t === "image_text" || t === "cta_band") && (
                <AiTextField label="Body text" value={c.body} onChange={(v) => setC({ body: v })} rich field="body" sectionType={t} pageName={pageName} testid="drawer-body" />
              )}
              {t === "image_text" && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Image side</Label>
                  <Select value={c.image_side || "left"} onValueChange={(v) => setC({ image_side: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="drawer-image-side"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[90]"><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* Media */}
          {HAS_MEDIA.has(t) && <AiImageField media={section.media} onPatch={setM} />}
          {HAS_VIDEO.has(t) && <AiVideoField media={section.media} onPatch={setM} />}
          {t === "hero" && <SlidesEditor slides={section.media?.slides || []} onChange={(s) => setM({ slides: s })} />}

          {/* Overlay */}
          {HAS_OVERLAY.has(t) && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Overlay</Label>
              <Select value={section.overlay?.type || "none"} onValueChange={(v) => setO({ type: v })}>
                <SelectTrigger data-testid="drawer-overlay-type"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[90]">{OVERLAY_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Opacity / darkness — {section.overlay?.opacity ?? 0}%</Label>
                <Slider value={[section.overlay?.opacity ?? 0]} min={0} max={100} step={1} onValueChange={([v]) => setO({ opacity: v })} className="mt-2" disabled={(section.overlay?.type || "none") === "none"} data-testid="drawer-overlay-opacity" />
              </div>
            </div>
          )}

          {/* Buttons */}
          {t !== "feature_grid" && t !== "gallery" && (
            <ButtonsEditor buttons={section.buttons || []} onChange={(b) => onPatch(null, { buttons: b })} />
          )}

          {(t === "feature_grid" || t === "gallery") && (
            <p className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">Item-level editing for this section type is managed in the form editor.</p>
          )}

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Internal label (editors only)</Label>
            <Input value={section.label || ""} onChange={(e) => onPatch(null, { label: e.target.value })} className="mt-1.5" data-testid="drawer-label" />
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 -mx-6 border-t border-border bg-background px-6 py-3">
          <button type="button" onClick={async () => { const ok = await onSave(); if (ok) onOpenChange(false); }} disabled={saving} data-testid="drawer-save" className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Save className="h-4 w-4" /> Save changes</>}
          </button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
